import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from '@/assets/img/acc-pay-illustration-banner.svg'


const _withScopeId = n => (_pushScopeId("data-v-103466c3"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "acc-pay-banner",
  role: "banner"
}
const _hoisted_2 = {
  class: "acc-pay-banner__illustration",
  "aria-hidden": "true"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("img", {
  src: _imports_0,
  alt: "Accounting and Payments illustration",
  class: "acc-pay-banner__illustration-img"
}, null, -1 /* HOISTED */))
const _hoisted_4 = { class: "acc-pay-banner__body" }
const _hoisted_5 = { class: "acc-pay-banner__title" }
const _hoisted_6 = { class: "acc-pay-banner__description" }
const _hoisted_7 = {
  key: 1,
  class: "acc-pay-banner-success"
}
const _hoisted_8 = ["dsf_title"]

import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ANALYTICS_EVENTS } from '@/services/analyticsEvents'
import { analytics } from '@/services/analyticsProvider'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { getStateData } from '@/store/getters'
import AccPayModal from './AccPayModal.vue'
import {
  MicrofrontendEvents,
  onDispatchToastNotification,
  publish,
} from '@sennder/senn-node-microfrontend-event-bus'
import OpsExternalInteractionsService from '@/services/opsExternalInteractionsService'

const ACC_PAY_BANNER_DISMISS_COUNT_LS_KEY = 'acc_pay_banner_dismiss_count'
const ACC_PAY_BANNER_ACTIVATED_LS_KEY = 'acc_pay_banner_activated'
const ACC_PAY_BANNER_ACTIVATED_DISMISSED_LS_KEY =
  'acc_pay_banner_activated_dismissed'
const MAX_DISMISS_COUNT = 2


export default /*#__PURE__*/_defineComponent({
  __name: 'AccPayBanner',
  setup(__props) {

const { t } = useI18n()

const isLearnMoreModalOpen = ref(false)
const accPayBannerDismissed = ref(false)
const isActivatedBannerDismissed = ref(
  window.localStorage.getItem(ACC_PAY_BANNER_ACTIVATED_DISMISSED_LS_KEY) ===
    'true'
)

const onActivatedBannerDismiss = () => {
  window.localStorage.setItem(ACC_PAY_BANNER_ACTIVATED_DISMISSED_LS_KEY, 'true')
  isActivatedBannerDismissed.value = true
}

const stateData = computed(() => getStateData())

const carrierId = computed(
  () => stateData.value.company?.cpsCarrierId || stateData.value.cpsCarrier?.id
)

const isCarrierIncludedInFlag = useFeatureFlag('ENABLE_ACCELERATED_PAYMENT')

const hasAcceleratedPaymentTerms = computed(() => {
  const billing = stateData.value.cpsCarrier?.billing
  return (
    Number(billing?.invoicing_term_days) === 3 &&
    Number(billing?.discount_percent) === 3
  )
})

const getDismissClickCount = (): number => {
  const raw = window.localStorage.getItem(ACC_PAY_BANNER_DISMISS_COUNT_LS_KEY)

  if (!raw) return 0

  const parsed = Number(raw)

  if (!Number.isInteger(parsed) || parsed < 0) {
    window.localStorage.removeItem(ACC_PAY_BANNER_DISMISS_COUNT_LS_KEY)
    return 0
  }

  return parsed
}

onMounted(() => {
  accPayBannerDismissed.value =
    getDismissClickCount() >= MAX_DISMISS_COUNT ||
    window.localStorage.getItem(ACC_PAY_BANNER_ACTIVATED_LS_KEY) === 'true'

  if (isBannerVisible.value) {
    analytics.trackEvent(ANALYTICS_EVENTS.ACC_PAY_BANNER_IMPRESSED, {
      carrier_id: carrierId.value,
    })
  }
})

const isBannerVisible = computed(() => {
  return (
    isCarrierIncludedInFlag.value &&
    !accPayBannerDismissed.value &&
    !hasAcceleratedPaymentTerms.value
  )
})

watch(isBannerVisible, (visible) => {
  if (visible) {
    analytics.trackEvent(ANALYTICS_EVENTS.ACC_PAY_BANNER_IMPRESSED, {
      carrier_id: carrierId.value,
    })
  }
})

const isActivatedBannerVisible = computed(() => {
  return (
    isCarrierIncludedInFlag.value &&
    hasAcceleratedPaymentTerms.value &&
    !isActivatedBannerDismissed.value
  )
})

const onBannerDismiss = () => {
  const nextCount = getDismissClickCount() + 1

  window.localStorage.setItem(
    ACC_PAY_BANNER_DISMISS_COUNT_LS_KEY,
    String(nextCount)
  )
  // Hide immediately; persistence threshold checked on next mount
  accPayBannerDismissed.value = true

  analytics.trackEvent(ANALYTICS_EVENTS.ACC_PAY_BANNER_DISMISS, {
    carrier_id: carrierId.value,
  })
}

const onCtaClick = () => {
  isLearnMoreModalOpen.value = true
  analytics.trackEvent(ANALYTICS_EVENTS.ACC_PAY_BANNER_CTA_CLICK, {
    carrier_id: carrierId.value,
  })
}

const MODAL_CLOSE_EVENTS: Record<string, string> = {
  dismiss: ANALYTICS_EVENTS.ACC_PAY_MODAL_DISMISS,
  not_now: ANALYTICS_EVENTS.ACC_PAY_MODAL_NOT_NOW,
}

const onModalClose = (reason: 'dismiss' | 'not_now') => {
  isLearnMoreModalOpen.value = false
  analytics.trackEvent(MODAL_CLOSE_EVENTS[reason], {
    carrier_id: carrierId.value,
  })
}

const onModalActivate = async () => {
  isLearnMoreModalOpen.value = false
  accPayBannerDismissed.value = true

  // Persist activation so the banner never reappears
  window.localStorage.setItem(ACC_PAY_BANNER_ACTIVATED_LS_KEY, 'true')

  publish<onDispatchToastNotification>({
    name: MicrofrontendEvents.onDispatchToastNotification,
    data: {
      message: t('acc_pay.modal.toast.message'),
      title: t('acc_pay.modal.toast.title'),
      category: 'success',
      icon: 'Check',
    },
  })

  analytics.trackEvent(ANALYTICS_EVENTS.ACC_PAY_MODAL_ACTIVATE, {
    carrier_id: carrierId.value,
  })

  const carrierName = stateData.value.company?.name
  const userId = stateData.value.profile?.id
  if (carrierName && userId)
    await OpsExternalInteractionsService.sendFastPayEmail(carrierName, userId)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isBannerVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createCommentVNode(" Illustration "),
          _createElementVNode("div", _hoisted_2, [
            _renderSlot(_ctx.$slots, "illustration", {}, () => [
              _hoisted_3
            ])
          ]),
          _createCommentVNode(" Content "),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('acc_pay.banner.title')), 1 /* TEXT */),
            _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('acc_pay.banner.description')), 1 /* TEXT */),
            _createElementVNode("dsf-button", {
              class: "acc-pay-banner__cta",
              dsf_level: "primary",
              dsf_size: "small",
              onClick: onCtaClick
            }, _toDisplayString(_ctx.$t('acc_pay.banner.cta')), 1 /* TEXT */)
          ]),
          _createElementVNode("dsf-icon", {
            class: "acc-pay-banner__close",
            dsf_name: "X",
            dsf_size: "20px",
            onClick: onBannerDismiss
          })
        ]))
      : (isActivatedBannerVisible.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("dsf-banner", {
              dsf_dismissible: "",
              dsf_icon: "BadgeEuro",
              dsf_level: "success",
              dsf_size: "medium",
              dsf_title: _ctx.$t('acc_pay.banner.activated.title'),
              onDismiss: onActivatedBannerDismiss
            }, _toDisplayString(_ctx.$t('acc_pay.banner.activated.message')), 41 /* TEXT, PROPS, HYDRATE_EVENTS */, _hoisted_8)
          ]))
        : _createCommentVNode("v-if", true),
    (isLearnMoreModalOpen.value)
      ? (_openBlock(), _createBlock(AccPayModal, {
          key: 2,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (onModalClose('dismiss'))),
          onNotNow: _cache[1] || (_cache[1] = ($event: any) => (onModalClose('not_now'))),
          onActivate: onModalActivate
        }))
      : _createCommentVNode("v-if", true)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})